import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Spinner } from "react-bootstrap";
import { XCircleFill } from "react-bootstrap-icons";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiChevronUp, mdiClose } from "@mdi/js";
import {
  dismissWidgetRow,
  retryAIProcessingItem,
  toggleAIProcessingExpanded,
} from "./slice";
import InfoIcon from "../../images/info.svg";
import LimitIcon from "../../images/limit.svg";
import MaintenanceIcon from "../../images/maintenance.svg";
import RecommendationIcon from "../../images/recommendation.svg";
import HazardIcon from "../../images/hazard.svg";

// Overview returns null — caller renders a placeholder to keep rows aligned.
const iconFor = ({ type, concernLevel }) => {
  if (type === "Info") return InfoIcon;
  if (type === "Limit") return LimitIcon;
  if (type === "Issue") {
    if (concernLevel === 2) return HazardIcon;
    if (concernLevel === 1) return RecommendationIcon;
    return MaintenanceIcon;
  }
  return null; // Overview
};

const isComplete = (item) => item.taskState === "SUCCESS";
const isFailed = (item) => item.taskState === "FAILURE";

// Returns null until the post-SUCCESS refetch lands — caller falls back to
// the user's original note hint.
const findItemName = (reportContent, itemId) => {
  if (!itemId || !reportContent?.sections) return null;
  for (const section of reportContent.sections) {
    for (const overview of section.overviews || []) {
      if (overview.id === itemId) return overview.name || null;
    }
    for (const subsection of section.subsections || []) {
      const buckets = [
        subsection.issues,
        subsection.infos,
        subsection.limits,
        subsection.overviews,
      ];
      for (const bucket of buckets) {
        for (const item of bucket || []) {
          if (item.id === itemId) return item.name || null;
        }
      }
    }
  }
  return null;
};

// Processing → user note. Complete → AI-generated item name from the
// structured report. Overview always reads "Overview" per design.
const labelFor = (item, reportContent) => {
  if (item.type === "Overview") return "Overview";
  if (isComplete(item)) {
    const itemName = findItemName(reportContent, item.itemId);
    if (itemName) return itemName;
  }
  return item.displayName || "";
};

const AIProcessingWidget = ({ onViewItem }) => {
  const dispatch = useDispatch();
  const { items, isExpanded } = useSelector(
    (state) => state.webReportEdit.aiProcessing
  );
  const reportContent = useSelector(
    (state) => state.webReportEdit.reportContent
  );

  // The ✕ clears the log row only (widgetDismissed) without removing the item
  // or its card, so hidden rows drop out of the running log here.
  const rows = items.filter((i) => !i.widgetDismissed);
  if (!rows.length) return null;

  const inProgressCount = rows.filter(
    (i) => !isComplete(i) && !isFailed(i)
  ).length;

  const handleView = (item) => {
    onViewItem?.(item.itemId);
  };

  const handleRetry = (item) => {
    dispatch(retryAIProcessingItem({ taskId: item.taskId }));
  };

  return (
    <div className="ai-processing-widget">
      <button
        type="button"
        className="ai-processing-header"
        onClick={() => dispatch(toggleAIProcessingExpanded())}
      >
        <span className="ai-processing-title">AI Processing</span>
        <span className="ai-processing-header-right">
          <span className="ai-processing-count">
            {inProgressCount} {inProgressCount === 1 ? "item" : "items"}
          </span>
          <Icon
            path={isExpanded ? mdiChevronUp : mdiChevronDown}
            size={0.75}
          />
        </span>
      </button>
      {isExpanded && (
        <ul className="ai-processing-list">
          {rows.map((item) => {
            const icon = iconFor(item);
            const complete = isComplete(item);
            const failed = isFailed(item);
            return (
              <li key={item.taskId} className="ai-processing-row">
                {icon ? (
                  <img
                    src={icon}
                    alt=""
                    className="ai-processing-type-icon"
                  />
                ) : (
                  <span className="ai-processing-type-icon-placeholder" />
                )}
                <span className="ai-processing-photo-count">
                  {item.photoCount}{" "}
                  {item.photoCount === 1 ? "photo" : "photos"}
                </span>
                <span className="ai-processing-display-name">
                  {labelFor(item, reportContent)}
                </span>
                <span className="ai-processing-status">
                  {complete && (
                    <>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ai-processing-view-btn"
                        onClick={() => handleView(item)}
                      >
                        View
                      </Button>
                      <button
                        type="button"
                        className="ai-processing-dismiss"
                        aria-label="Dismiss"
                        onClick={() =>
                          dispatch(dismissWidgetRow(item.taskId))
                        }
                      >
                        <Icon path={mdiClose} size={0.667} />
                      </button>
                    </>
                  )}
                  {failed && (
                    <>
                      <XCircleFill className="ai-processing-failed-icon" />
                      <Button
                        variant="primary"
                        size="sm"
                        className="ai-processing-view-btn"
                        onClick={() => handleRetry(item)}
                      >
                        Retry
                      </Button>
                      <button
                        type="button"
                        className="ai-processing-dismiss"
                        aria-label="Dismiss"
                        onClick={() =>
                          dispatch(dismissWidgetRow(item.taskId))
                        }
                      >
                        <Icon path={mdiClose} size={0.667} />
                      </button>
                    </>
                  )}
                  {!complete && !failed && (
                    <Spinner
                      animation="grow"
                      size="sm"
                      variant="primary"
                      className="ai-processing-spinner"
                    />
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AIProcessingWidget;
